import { z } from "zod";
import type { Locale } from "./common";
import { patientGroupSchema } from "./patient-group";
import { meetingPrescriptionOrderSchema } from "./prescription-cycle";
import {
  WEBDOC_JOURNAL_UNCONFIRMED,
  WEBDOC_LAB_ORDER_UNCONFIRMED,
  webdocControlSchema,
  webdocJournalConfirmed,
  webdocLabOrderConfirmed,
} from "./prescription-choice";
import {
  DOCTOR_NOTICE_UNCONFIRMED_CLAIM,
  doctorNoticeBodySchema,
  doctorNoticeUnconfirmedClaims,
} from "./doctor-notice";
import {
  preConsultRiskFlagSchema,
  preConsultSummarySchema,
} from "./preconsult-summary";
import {
  completionItemSchema,
  completionOutcomeSchema,
  meetingCompletionDecisionSchema,
} from "./completion-picker";

// CAR-14 — video consult booking + room minting. Same DTO crosses
// patient mobile (UpcomingMeeting) and clinician admin
// (ClinicianMeeting); the variant fields are in extra optional
// properties on the patient shape.

export const meetingReasonSchema = z.enum([
  "first_consult",
  "renewal_review",
  "follow_up",
]);
export type MeetingReason = z.infer<typeof meetingReasonSchema>;

export const meetingStatusSchema = z.enum([
  "scheduled",
  "in_progress",
  "completed",
  "cancelled",
  "no_show",
]);
export type MeetingStatus = z.infer<typeof meetingStatusSchema>;

// TES-521 — when a call can actually be joined. One rule for the admin
// button, the app's Hem card and the API's token mint, so they cannot drift
// apart again. The lead is ALSO the Daily token's not-before (CAR-52): a
// token minted earlier is inert, and both clients used to offer "Anslut"
// for a 24-hour window in which Daily refused every token — so the window
// is the token's window, 15 minutes before the start. The meeting is over
// once (start + duration + grace) has passed — the same 15-minute grace the
// meeting-state sweep uses before flipping a row to no_show / completed. A
// meeting that the sweep already closed (or that was cancelled) is never
// joinable, whatever the clock says.
export const MEETING_JOIN_LEAD_MINUTES = 15;
export const MEETING_JOIN_LEAD_MS = MEETING_JOIN_LEAD_MINUTES * 60 * 1000;
export const MEETING_JOIN_GRACE_MINUTES = 15;

// TES-1383 — what the doctor's open meeting view re-reads while it is open.
// A patient who moves her meeting cancels the old row and books a new one
// (BookingService.reschedule), so "cancelled" alone cannot tell the doctor
// whether the meeting is gone or only moved. `rescheduledTo` is the live
// meeting at the end of the reschedule chain; `cancelledBy` says whose
// cancellation it was.
export const meetingLiveStatusSchema = z.object({
  id: z.string(),
  status: meetingStatusSchema,
  scheduledAt: z.string(),
  cancelledBy: z.enum(["patient", "clinic"]).nullable(),
  rescheduledTo: z
    .object({
      id: z.string(),
      patientId: z.string(),
      scheduledAt: z.string(),
    })
    .nullable(),
});
export type MeetingLiveStatus = z.infer<typeof meetingLiveStatusSchema>;

// TES-1305 (Simon 2026-10-13) — how far ahead a patient can book: four weeks.
// One number for the API's slot listing and admin's "no slots" warning, so
// the two cannot disagree again.
export const BOOKING_HORIZON_DAYS = 28;

// TES-1288 (TES-1267 §1, Emelie 2026-10-12) — the meeting length the patient
// is told, everywhere: "Digitalt vårdmöte · 15 minuter". It is deliberately
// NOT the doctor's slotMinutes / slotIntervalMinutes: "Läkarens kalender
// reserverar tills vidare 30 minuter per ny patient … Patienten ska inte se
// det interna efterarbetet som en 30-minuters mötestid." So
// `Meeting.durationMinutes` keeps driving the doctor's calendar, the join
// window and the Daily room; patient copy and the patient's own calendar
// entry use this.
export const PATIENT_MEETING_MINUTES = 15;

export type MeetingJoinability = "too_early" | "open" | "over";

export function meetingJoinability(
  meeting: {
    scheduledAt: string | Date;
    durationMinutes: number;
    status: MeetingStatus;
  },
  now: number = Date.now(),
): MeetingJoinability {
  if (meeting.status !== "scheduled" && meeting.status !== "in_progress") {
    return "over";
  }
  const start = new Date(meeting.scheduledAt).getTime();
  if (start - now > MEETING_JOIN_LEAD_MS) return "too_early";
  const end =
    start + (meeting.durationMinutes + MEETING_JOIN_GRACE_MINUTES) * 60 * 1000;
  return now > end ? "over" : "open";
}

export const bookableSlotSchema = z.object({
  doctorId: z.string(),
  doctorDisplayName: z.string(),
  startAt: z.string(),
  durationMinutes: z.number().int().positive(),
});
export type BookableSlot = z.infer<typeof bookableSlotSchema>;

// Patient view — exposes the doctor identity + the daily room name
// (used by the mobile join screen to compare against the room the JWT
// is for).
export const upcomingMeetingSchema = z.object({
  id: z.string().uuid(),
  scheduledAt: z.string(),
  durationMinutes: z.number().int(),
  reason: meetingReasonSchema,
  status: meetingStatusSchema,
  dailyRoomName: z.string().nullable(),
  doctor: z.object({
    id: z.string().uuid(),
    displayName: z.string(),
    // TES-719 — the media key of this doctor's portrait, resolved by the
    // server (own, else the tenant's doctor role slot; null = neither).
    // Optional so an older API response still parses.
    avatarMediaKey: z.string().nullable().optional(),
  }),
  // TES-1486 (Ö-678) — the last moment she can move this meeting for free
  // (ISO); after it the server refuses the move. Null when no limit applies
  // (the meeting carries no fee). Optional so an older API response still
  // parses: a client without it shows no terms and the server stays the gate.
  freeRescheduleUntil: z.string().nullable().optional(),
  // TES-1539 (Ö-820, Ö-842) — what a change after freeRescheduleUntil costs:
  // a move always costs `amountCents`, paid before the move; a cancellation
  // costs it when `cancelCharges` (the meeting was free or included), and
  // otherwise forfeits the fee already paid. Null when no late-change fee
  // applies (LATE_CHANGE_FEE_ENABLED off); absent for a client before
  // CLIENT_CONTRACT_CHANGES.lateChangeFee.
  lateChangeFee: z
    .object({
      amountCents: z.number().int().nonnegative(),
      currency: z.string(),
      cancelCharges: z.boolean(),
    })
    .nullable()
    .optional(),
});
export type UpcomingMeeting = z.infer<typeof upcomingMeetingSchema>;
export type LateChangeFee = NonNullable<UpcomingMeeting["lateChangeFee"]>;

// TES-1485 (Ö-677) — one meeting, by id: "Ditt vårdmöte", the same object
// from "Hem", "Meddelanden" and "Din vård → Vårdmöten". Unlike the upcoming list it
// also answers for a meeting that has passed or was cancelled, so the view
// can say so. A meeting the patient moved resolves to the live meeting at the
// end of the reschedule chain (`id` is then that meeting's), because a link
// to the old one still means "my meeting". `sessionEndedAt` is the verified
// end of the video session (TES-739): once set, the meeting is over whatever
// its status row says.
export const patientMeetingSchema = upcomingMeetingSchema.extend({
  sessionEndedAt: z.string().nullable(),
});
export type PatientMeeting = z.infer<typeof patientMeetingSchema>;

/** How "Ditt vårdmöte" presents a meeting: still ahead, over, or cancelled. */
export type PatientMeetingPhase = "upcoming" | "passed" | "cancelled";

export function patientMeetingPhase(
  meeting: Pick<
    PatientMeeting,
    "scheduledAt" | "durationMinutes" | "status" | "sessionEndedAt"
  >,
  now: number = Date.now(),
): PatientMeetingPhase {
  if (meeting.status === "cancelled") return "cancelled";
  if (meeting.sessionEndedAt !== null) return "passed";
  return meetingJoinability(meeting, now) === "over" ? "passed" : "upcoming";
}

// TES-546 (TES-434 B) — the meeting Hem may still ask the patient to rate:
// the latest one that was held (completed, or documented by the doctor) in
// the last seven days and has no rating yet. The room asks on the way out;
// this is the second chance when the call was closed, or never joined.
export const MEETING_RATE_WINDOW_DAYS = 7;
export const meetingToRateSchema = z.object({
  id: z.string().uuid(),
  scheduledAt: z.string(),
  doctor: z.object({ id: z.string().uuid(), displayName: z.string() }),
});
export type MeetingToRate = z.infer<typeof meetingToRateSchema>;

// TES-1233 — "status för nödvändigt underlag" on a calendar block. Which
// evidence applies depends on the meeting: the health declaration (F-02)
// before a new-treatment first consult, F-07 before a transfer patient's
// meeting, and the open CP-06 completion checklist for everything else.
export const MEETING_EVIDENCE_KINDS = [
  "health_declaration",
  "prescription_transfer",
  "completion",
] as const;
export const meetingEvidenceSchema = z.object({
  kind: z.enum(MEETING_EVIDENCE_KINDS),
  status: z.enum(["complete", "missing"]),
});
export type MeetingEvidence = z.infer<typeof meetingEvidenceSchema>;

// Clinician view — exposes the patient identifier (last 6 of pnr) so
// the admin meeting list can show who the call is with.
export const clinicianMeetingSchema = z.object({
  id: z.string().uuid(),
  scheduledAt: z.string(),
  durationMinutes: z.number().int(),
  reason: meetingReasonSchema,
  status: meetingStatusSchema,
  // Documentation state — admin lists past undocumented meetings in
  // "Att dokumentera"; optional for older API responses.
  workflowStatus: z.string().optional(),
  patient: z.object({
    id: z.string().uuid(),
    // The doctor's meeting list identified the patient by six digits alone —
    // and by nothing at all for the dev/mock rows whose personnummer is empty.
    // Optional so an older API response still parses.
    name: z.string().nullable().optional(),
    personalNumberLast6: z.string(),
    // TES-1306 — "Reservnummer vid tekniska problem", E.164. Present (string
    // or null) for the treating doctor only; absent for an admin's support
    // read and from an older API, and the views then show nothing about it.
    backupPhone: z.string().nullable().optional(),
  }),
  // TES-1233 — what the calendar, "Nästa möte" and the missed-meeting
  // protection read. All optional so an older API response still parses.
  //
  // The patient's journey decides the meeting type the doctor sees:
  // a first consult is "Nybehandling" or "Receptövertag" by her group.
  patientGroup: patientGroupSchema.nullable().optional(),
  doctor: z.object({ id: z.string(), displayName: z.string() }).optional(),
  // When each side first took a Daily token for this meeting, i.e. pressed
  // "Anslut". Read from the `meeting.token_minted` audit rows; null = never.
  doctorJoinedAt: z.string().nullable().optional(),
  patientJoinedAt: z.string().nullable().optional(),
  // The doctor's verified end ("Avsluta mötet" or Daily's meeting.ended).
  sessionEndedAt: z.string().nullable().optional(),
  // F-02 submitted, and how many medical flags its latest submission raised.
  healthDeclarationSubmitted: z.boolean().optional(),
  medicalFlagCount: z.number().int().optional(),
  // The evidence this meeting depends on. Only the calendar fills it.
  evidence: meetingEvidenceSchema.nullable().optional(),
});
export type ClinicianMeeting = z.infer<typeof clinicianMeetingSchema>;

// ---------------------------------------------------------------------------
// TES-291 [J10] — managed care-meeting workflow (prep + documentation)
// ---------------------------------------------------------------------------

// The workflow track on a Meeting, orthogonal to `status` (the scheduling /
// Daily-video lifecycle). A linear ladder: `scheduled` is the birth state, the
// doctor runs the consult (→in_progress), then DOCUMENTS the decision artifact
// (→documented, terminal). Mirrors the shape of the handoff/review-item state
// machines. Forward-only; a jump straight from scheduled→documented is legal.
// `documented` is idempotent (a re-document overwrites the note).
//
// TES-766 (Emelie's point 13, 2026-09-17) — `prepped` IS REMOVED. It had
// been in the enum since TES-291 but no code ever set it; it described a prep
// view that was never built. Emelie:
// "Bygg inte ett extra produktsteg enbart för att enumvärdet finns."
//
// The column is a free string, so the removal is not a migration — but a
// historical row on `prepped` would no longer parse. No such rows exist
// locally (all 207 meetings are on `scheduled`), and QA/prod must be checked
// before deploy. Should one turn up, the right fix is to move it to
// `in_progress`, not to reintroduce the value.
export const MEETING_WORKFLOW_STATUSES = [
  "scheduled",
  "in_progress",
  "documented",
] as const;
export const meetingWorkflowStatusSchema = z.enum(MEETING_WORKFLOW_STATUSES);
export type MeetingWorkflowStatus = z.infer<typeof meetingWorkflowStatusSchema>;

export const MEETING_WORKFLOW_TRANSITIONS: Record<
  MeetingWorkflowStatus,
  readonly MeetingWorkflowStatus[]
> = {
  scheduled: ["in_progress", "documented"],
  in_progress: ["documented"],
  documented: [],
};

export function canTransitionMeetingWorkflow(
  from: MeetingWorkflowStatus,
  to: MeetingWorkflowStatus,
): boolean {
  return MEETING_WORKFLOW_TRANSITIONS[from].includes(to);
}

// Consult-fee payment state surfaced in the prep view. Mirrors the
// ConsultPayment lifecycle (pending → paid → consumed); `none` = no row yet.
export const meetingPaymentStatusSchema = z.enum([
  "none",
  "pending",
  "paid",
  "consumed",
]);
export type MeetingPaymentStatus = z.infer<typeof meetingPaymentStatusSchema>;

// The PRE-meeting prep dashboard: a read-only aggregation the doctor opens
// before the consult. Reuses the CAR-118 pre-consult summary + risk flags, the
// stored journey state, the consult-payment state, and the patient's goals
// (lifted from the intake `goals` section). `healthDeclaration.part2` is a
// FOLLOW-UP: there is no "part 2 / pre-meeting declaration" concept in the
// schema today, so `part2Required` is always false and `part2SubmittedAt` null
// — wire a real second declaration when the product introduces one.
// TES-506 — the documentation as it is STORED on the meeting.
//
// Split out from `meetingDocumentationSchema` (which is the response to a
// document call and additionally carries what that call ACTUATED). What was
// written is durable; what a particular call fired is not, and returning a
// stale `actuated` block on every prep read would have invited the doctor to
// read a handoff or a message as if it had just happened.
// TES-537 (Ö-4, A-02 "Minimal läkaradmin") — the ONE
// decision the checklist collects after a first consult:
// "Nästa steg efter vårdmötet", exactly one of four. It is what routes the
// patient:
//   upload_existing_labs        → CP-06 with upload + blood pressure/pulse
//   new_blood_test              → CP-06 with a new blood test + BP/pulse
//   labs_verified_trusted_source→ CP-06 with BP/pulse only
//   cannot_proceed              → no CP-06, the negative decision flow
// The older two booleans (labRequired/treatmentProceed) are DERIVED from it
// (`decisionsForNextStep`) so the journey, the CP-09 outcome events and every
// reader of the stored row keep their meaning. The prescription itself is
// written in Webdoc; the order the doctor proposes at the meeting is its own
// structured field (TES-956, `proposedOrder`).
export const MEETING_NEXT_STEPS = [
  "upload_existing_labs",
  "new_blood_test",
  "labs_verified_trusted_source",
  "cannot_proceed",
] as const;
export const meetingNextStepSchema = z.enum(MEETING_NEXT_STEPS);
export type MeetingNextStep = z.infer<typeof meetingNextStepSchema>;

export function decisionsForNextStep(step: MeetingNextStep): {
  labRequired: boolean;
  treatmentProceed: boolean;
} {
  switch (step) {
    case "upload_existing_labs":
    case "new_blood_test":
      // Labs first: no final decision yet (CP-09C), journey → labs_required.
      return { labRequired: true, treatmentProceed: false };
    case "labs_verified_trusted_source":
      return { labRequired: false, treatmentProceed: true };
    case "cannot_proceed":
      return { labRequired: false, treatmentProceed: false };
  }
}

// TES-434 B (Ö-88) — "Hur var ditt vårdmöte?", 1–5. An
// internal rating: the responsible doctor sees it on the patient page, the
// clinic in aggregate. Independent of the App Store prompt by design.
export const rateMeetingSchema = z.object({
  rating: z.number().int().min(1).max(5),
});
export type RateMeetingRequest = z.infer<typeof rateMeetingSchema>;

// TES-649 — the request bodies of book, reschedule and cancel. They used to
// exist only as TypeScript types on the controller parameters, which vanish at
// runtime; these are what the API parses at the boundary. Kept to what the
// mobile and admin clients send: `startAt` is the slot's own ISO string, so
// its date check (`invalid_start_at`) stays in the controller next to the
// `new Date()` it guards.
export const bookMeetingSchema = z.object({
  doctorId: z.string().min(1),
  startAt: z.string().min(1),
  reason: meetingReasonSchema,
});
export type BookMeetingRequest = z.infer<typeof bookMeetingSchema>;

export const rescheduleMeetingSchema = bookMeetingSchema.omit({
  reason: true,
});
export type RescheduleMeetingRequest = z.infer<typeof rescheduleMeetingSchema>;

// Both clients send `{}` when no reason was given, so the reason is optional
// and an absent body counts as an empty one.
export const cancelMeetingSchema = z
  .object({ reason: z.string().optional() })
  .default({});
export type CancelMeetingRequest = z.infer<typeof cancelMeetingSchema>;

export const meetingRatingStatsSchema = z.object({
  count: z.number().int().nonnegative(),
  average: z.number().nullable(),
  // Index 0 = one star … index 4 = five stars.
  distribution: z.array(z.number().int().nonnegative()).length(5),
});
export type MeetingRatingStats = z.infer<typeof meetingRatingStatsSchema>;

// TES-950 — the picker decision as stored on the meeting. Null on a meeting
// documented before the picker (its `nextStep` package is the record then).
export const meetingCompletionRecordSchema = z.object({
  outcome: completionOutcomeSchema,
  items: z.array(completionItemSchema),
});
export type MeetingCompletionRecord = z.infer<
  typeof meetingCompletionRecordSchema
>;

export const meetingDocumentationRecordSchema = z.object({
  assessment: z.string().nullable(),
  nextStep: meetingNextStepSchema.nullable(),
  prescriptionEvidenceRequired: z.boolean().nullable().optional(),
  // TES-950 — optional so an older API response still parses.
  completion: meetingCompletionRecordSchema.nullable().optional(),
  subscriptionInfoGiven: z.boolean().nullable(),
  labRequired: z.boolean().nullable(),
  treatmentProceed: z.boolean().nullable(),
  treatmentPlanNote: z.string().nullable(),
  // Legacy free text (TES-506). Read-only since TES-956; shown only when a
  // meeting has no structured order.
  prescription: z.string().nullable(),
  // TES-956 — the order proposed at the meeting. Null when nothing was
  // written; optional so an older API response still parses.
  proposedOrder: meetingPrescriptionOrderSchema.nullable().optional(),
  documentedByEmployeeId: z.string().nullable(),
  documentedByDisplayName: z.string().nullable(),
  documentedAt: z.string().nullable(),
});
export type MeetingDocumentationRecord = z.infer<
  typeof meetingDocumentationRecordSchema
>;

export const meetingPrepSchema = z.object({
  meeting: z.object({
    id: z.string().uuid(),
    scheduledAt: z.string(),
    durationMinutes: z.number().int(),
    reason: meetingReasonSchema,
    status: meetingStatusSchema,
    workflowStatus: meetingWorkflowStatusSchema,
    doctor: z.object({ id: z.string().uuid(), displayName: z.string() }),
    // TES-1228 — a meeting the patient booked herself, outside any
    // assessment: documented with the journal note and the Webdoc
    // confirmation only, no next step. Optional so an older API response
    // still parses (absent = documented as before).
    voluntary: z.boolean().optional(),
  }),
  patient: z.object({
    id: z.string().uuid(),
    // Optional so an older API response still parses — see `patientLabelSchema`.
    name: z.string().nullable().optional(),
    personalNumberLast6: z.string(),
    journeyState: z.string(),
    preferredLocale: z.string(),
    // TES-1306 — as on ClinicianMeeting.patient: the doctor's only.
    backupPhone: z.string().nullable().optional(),
  }),
  payment: z.object({
    status: meetingPaymentStatusSchema,
    // True when a consult fee has been paid (paid or consumed) — the booking
    // gate is satisfied.
    settled: z.boolean(),
  }),
  healthDeclaration: z.object({
    part1SubmittedAt: z.string().nullable(),
    part1Reviewed: z.boolean(),
    part2Required: z.boolean(),
    part2SubmittedAt: z.string().nullable(),
  }),
  riskFlags: z.array(preConsultRiskFlagSchema),
  goals: z.array(z.string()),
  preConsultSummary: preConsultSummarySchema.nullable(),
  // TES-506 — what has already been written about this meeting, if anything.
  //
  // The 2026-09-04 walkthrough: "the notes cannot be found afterwards (nor can
  // the prescription)." The prep response carried the meeting's workflowStatus
  // but never its content, so the form opened empty even for a documented
  // meeting and the doctor had nowhere to read her own note. Nullable: null =
  // undocumented.
  documentation: meetingDocumentationRecordSchema.nullable(),
});
export type MeetingPrep = z.infer<typeof meetingPrepSchema>;

// The POST-meeting documentation input (POST …/document). The clinician's
// assessment + the two gate decisions, plus OPTIONAL actuation payloads that
// the service fulfils by reusing existing primitives:
//   - coachHandoff  → HandoffService.create (doctor→coach)
//   - patientNotice → "Läkarens besked till patienten · visas under Din vård"
//     (TES-1201), published as a medical notice she reads under Din vård.
//     It replaced `patientFeedback`, a doctor chat message (TES-506): the
//     doctor has no patient chat any more. Still published directly, with
//     no second clinician's review (the 2026-09-04 walkthrough: "The
//     feedback does not need reviewing, it can go out directly.").
// `treatmentProceed` additionally advances the journey + fires the next-flow
// event; `labRequired` advances the journey to labs_required.
export const documentMeetingSchema = z
  .object({
    // TES-506 — OPTIONAL. The 2026-09-04 walkthrough: "the assessment can go".
    // It was required, and a required field the doctor does not want to fill in
    // gets filled with junk to get past it — which is worse than an empty
    // field, because the junk looks like an assessment in the record.
    assessment: z.string().trim().max(10000).optional(),
    // TES-537 — the two checklist fields (A-02). Everything the journey needs
    // follows from `nextStep`; the booleans are derived server-side.
    //
    // TES-950 — optional now: the admin sends `completion` instead and the
    // server derives `nextStep` from it (`meetingNextStepForCompletion`). An
    // older client that still sends a package is accepted as before. Exactly
    // one of the two — see the refinement below.
    nextStep: meetingNextStepSchema.optional(),
    // TES-950 — the shared completion picker: proceed / completion (with
    // exactly the chosen items) / cannot proceed, labelled in
    // COMPLETION_OUTCOME_LABEL. Replaces the fixed packages and the separate
    // prescription-evidence checkbox.
    completion: meetingCompletionDecisionSchema.optional(),
    // TES-590 — a SEPARATE choice that combines with the
    // blood-test branch above:
    // "Recept-/ordinationsunderlag behöver laddas upp". Never a fifth next
    // step, so a doctor can ask for a new blood test
    // AND prescription evidence at once.
    prescriptionEvidenceRequired: z.boolean().optional().default(false),
    // TES-997 (Ö-259) — "Muntlig abonnemangsinformation" is gone from
    // doctor admin and "får inte blockera publicering". It was REQUIRED
    // here, so a client that no longer sends it would have been refused.
    // Still accepted from an older client and stored as sent; never asked.
    subscriptionInfoGiven: z.boolean().optional(),
    treatmentPlanNote: z.string().trim().max(10000).optional(),
    // TES-506 gave this a box; TES-537 (A-02) took it out of the checklist —
    // the prescription is written in Webdoc. The column stays for history;
    // an older client may still send it.
    prescription: z.string().trim().max(4000).optional(),
    // TES-956 (TES-941): "Receptinformationen ska bara registreras en gång."
    // The order proposed at the meeting — medication, dose, frequency and
    // length — entered once here and reused by the journal and the decision.
    // Absent = leave what is stored; all four blank = clear it.
    proposedOrder: meetingPrescriptionOrderSchema.optional(),
    coachHandoff: z
      .object({
        reason: z.string().trim().min(1).max(2000),
        priority: z.enum(["low", "medium", "high", "urgent"]).optional(),
        context: z.string().trim().max(4000).optional(),
      })
      .optional(),
    // TES-1201 — one coherent text, prefilled in the admin
    // (buildDoctorNoticePrefill); headings left empty are dropped at publish
    // (finalizeDoctorNotice). Swedish only, like every medical notice.
    patientNotice: doctorNoticeBodySchema.optional(),
    // TES-804 (Ö-225) — the SUCCESSOR writing up a consult she did not hold.
    //
    // Emelie 2026-09-23, on a handover taken while the original doctor still
    // owed a note:
    // "noten ska fortfarande skrivas — av efterträdaren, som en markerad retrospektiv anteckning".
    // Four things are compulsory in it: the
    // original treater, the meeting date, the sources, and why the original
    // doctor could not finish.
    //
    // TWO of them are here and two are not, deliberately. The original treater
    // and the date are already columns on the meeting, so asking a human to
    // retype them would only create a way for the note to contradict the
    // record. These two exist nowhere but in the successor's head.
    //
    // Presence of this object IS the request to write someone else's meeting
    // up. Without it the server refuses the write outright, so a doctor cannot
    // document a colleague's consult by accident and have it read as her own.
    retrospective: z
      .object({
        sources: z.string().trim().min(1).max(10000),
        originalDoctorUnavailableReason: z.string().trim().min(1).max(2000),
      })
      .optional(),
    // TES-997 (Ö-259) — the control step
    // "Slutför i Webdoc innan du publicerar".
    // This call IS "Bekräfta och publicera till patienten": it
    // publishes the feedback and creates the patient's tasks. When the
    // decision asks for a new blood test, the doctor confirms the remiss is
    // placed in Webdoc before any of that happens. A meeting never decides a
    // prescription (that is the treatment decision, the transfer review or
    // the monthly review), so there is no prescription choice here.
    //
    // TES-1019 (Emelie, point 7) — and, always, that the finished note and
    // its ordination are entered in Webdoc. Required on every publish: the
    // admin is a web SPA deployed together with the API, so no older client
    // sends this route without it. The confirmed publish stores the note's
    // final version and its Webdoc receipt
    // (MeetingJournalService.recordPublished).
    webdocControl: webdocControlSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if (!webdocJournalConfirmed(v.webdocControl)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["webdocControl", "journalInWebdoc"],
        message: WEBDOC_JOURNAL_UNCONFIRMED,
      });
    }
    const newBloodTest = v.completion
      ? v.completion.outcome === "completion" &&
        v.completion.items.includes("new_blood_test")
      : v.nextStep === "new_blood_test";
    if (newBloodTest && !webdocLabOrderConfirmed(v.webdocControl)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["webdocControl", "labOrderDoneInWebdoc"],
        message: WEBDOC_LAB_ORDER_UNCONFIRMED,
      });
    }
    // TES-1201 — the notice must not say a remiss, lab order or prescription
    // exists before it is confirmed in Webdoc. A meeting never confirms a
    // prescription.
    if (
      v.patientNotice &&
      doctorNoticeUnconfirmedClaims(v.patientNotice, {
        labOrder: webdocLabOrderConfirmed(v.webdocControl),
        prescription: false,
      }).length > 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["patientNotice"],
        message: DOCTOR_NOTICE_UNCONFIRMED_CLAIM,
      });
    }
    // One decision, stated once. Both would let the package and the picker
    // disagree about what the patient was asked for.
    // TES-1228 — at most one here: a voluntary care meeting is documented
    // with neither (journal note and Webdoc confirmation only). Which
    // meetings must carry one is the server's call, since only it knows the
    // meeting (MeetingWorkflowService.documentMeeting).
    if (v.nextStep && v.completion) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["completion"],
        message: "exactly_one_of_next_step_or_completion",
      });
    }
    // The picker carries the prescription evidence as one of its items.
    if (v.completion && v.prescriptionEvidenceRequired) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["prescriptionEvidenceRequired"],
        message: "prescription_evidence_is_a_completion_item",
      });
    }
  });
export type DocumentMeetingRequest = z.input<typeof documentMeetingSchema>;

// The persisted documentation artifact returned after documenting.
export const meetingDocumentationSchema = z.object({
  id: z.string().uuid(),
  workflowStatus: meetingWorkflowStatusSchema,
  assessment: z.string().nullable(),
  nextStep: meetingNextStepSchema.nullable(),
  prescriptionEvidenceRequired: z.boolean().nullable().optional(),
  completion: meetingCompletionRecordSchema.nullable().optional(),
  subscriptionInfoGiven: z.boolean().nullable(),
  labRequired: z.boolean().nullable(),
  treatmentProceed: z.boolean().nullable(),
  treatmentPlanNote: z.string().nullable(),
  prescription: z.string().nullable(),
  proposedOrder: meetingPrescriptionOrderSchema.nullable().optional(),
  documentedByEmployeeId: z.string().nullable(),
  documentedAt: z.string().nullable(),
  // TES-804 (Ö-225) — the marking, so no surface can render a retrospective
  // note as the treating doctor's own words. Derived from
  // `documentedByEmployeeId !== doctorId` rather than stored: a boolean
  // beside the two ids would be a second place for the same fact to be
  // wrong. Null on a meeting nobody has documented yet.
  retrospective: z
    .object({
      // The doctor who actually held the consult, and when.
      originalDoctorId: z.string(),
      heldAt: z.string(),
      sources: z.string().nullable(),
      originalDoctorUnavailableReason: z.string().nullable(),
    })
    .nullable(),
  // What the actuation fired (so the admin can confirm the side-effects).
  actuated: z.object({
    journeyAdvancedTo: z.string().nullable(),
    coachHandoffId: z.string().nullable(),
    // TES-1201 — the medical notice the doctor's notice became (it was the
    // doctor chat message, `patientFeedbackMessageId`, before).
    patientNoticeId: z.string().nullable(),
    // TES-506 / Ö-59 — whether documenting also CLOSED the meeting. Answered by
    //  ("avsluta / godkänna vårdmötet"): it does. False when the
    // meeting was already completed, cancelled or a no-show — documenting must
    // never resurrect one of those.
    meetingCompleted: z.boolean(),
    nextFlowTriggered: z.boolean(),
  }),
});
export type MeetingDocumentation = z.infer<typeof meetingDocumentationSchema>;

// TES-739 — CP-05, the card between a verified session end and the doctor's
// documentation. Emelie's words verbatim (Drive CP-05, 2026-09-16); no CTA
// because "Se nästa steg" must not exist before a real next step does.
// Code-derived like CP-06's neutral wait (Ö-136), not an imported package:
// the state it reflects is a Meeting column, not a flow assignment.
export const CONSULT_ENDED_COPY: Record<
  Locale,
  { title: string; body: string }
> = {
  sv: {
    title: "Vårdmötet är genomfört",
    body: "Tack för idag. Ditt nästa steg visas här i Ella när läkaren har gått igenom underlaget.",
  },
  en: {
    title: "Your consultation is complete",
    body: "Thank you for today. Your next step will appear here in Ella once the doctor has reviewed your information.",
  },
};
